import { Module } from "owa-workloads-module";
import { bootstrapNovaCache, registerNovaTypePolicies } from "owa-nova-cache";
import { registerM365AcquisitionsTypePolicies, bootstrapApolloCaches, } from "owa-m365-acquisitions-cache";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isAddinMultiAccountEnabled } from "owa-feature-flags";

// Necessary to ensure the app bar selection state is initialized when the app is first loaded.
/* eslint-disable-next-line owa-custom-rules/forbid-empty-import-statements-from-modules -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
 *	> Empty import statements from non-relative modules are not allowed. Either import something specific or remove the import. */
import "owa-appbar-state";

import type { NormalizedCacheObject, ApolloClient } from "@apollo/client";
import type { BootstrapOptions } from "../types/BootstrapOptions";
import { trySetBposNavBarData } from "owa-bpos-store";
import { getAccountScopeUserSettings } from "owa-session-store";
import { markFunction } from "owa-performance";

/**
 * Bootstraps apollo cache promise
 * @param apolloClient
 * @param options Bootstrap options
 * @returns Apollo Client
 */
export const bootstrapApolloCache: (apolloClient: ApolloClient<NormalizedCacheObject>, options: BootstrapOptions, themePromise: Promise<void>) => Promise<ApolloClient<NormalizedCacheObject>> = markFunction(async function (apolloClient: ApolloClient<NormalizedCacheObject>, options: BootstrapOptions, themePromise: Promise<void>): Promise<ApolloClient<NormalizedCacheObject>> {
    // We need to cast it here to avoid a circular dep with owa-app-module's
    // AppBootstrapOptions declaration, which extends this module's BootstrapOptions.
    type AppBootstrapOptions = BootstrapOptions & {
        appModuleProps: {
            module: string;
        };
    };
    const currentModule: string | undefined = (options as AppBootstrapOptions).appModuleProps
        ?.module;
    
    if (!currentModule || !shouldPrimeAppBarCache(currentModule)) {
        return apolloClient;
    }
    
    const mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    
    if (isAddinMultiAccountEnabled()) {
        const userSettings = getAccountScopeUserSettings(mailboxInfo);
        if (userSettings.NavBarData) {
            trySetBposNavBarData(userSettings.NavBarData ?? null, mailboxInfo);
        }
    }
    // Primes local cached catalog and is integrated to Taos MOS3 app service
    await themePromise;
    
    registerM365AcquisitionsTypePolicies(apolloClient);
    await bootstrapApolloCaches(isAddinMultiAccountEnabled() ? mailboxInfo : undefined);
    registerNovaTypePolicies(apolloClient);
    bootstrapNovaCache();
    
    return apolloClient;
}, "apollo");

export type BootstrapApolloCacheFunc = typeof bootstrapApolloCache;

/**
 * Determines whether app bar is enabled for a given module
 * @param currentModule
 * @returns true if app bar enabled
 */
function shouldPrimeAppBarCache(currentModule: string): boolean {
    const appBarEnabled = [
        Module.Mail,
        Module.Calendar,
        Module.AppHost,
        Module.People,
        Module.FilesHub,
        Module.Groups,
    ];
    
    return appBarEnabled.indexOf(currentModule as Module) !== -1;
}
